import * as Sentry from '@sentry/react';

const VITE_SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;

/**
 * Initialize Sentry error tracking
 * Call this before rendering the React app
 */
export const initSentry = (): void => {
    if (typeof VITE_SENTRY_DSN !== "string" || VITE_SENTRY_DSN.trim() === "") {
        throw new Error("Sentry DSN is not defined or is empty")
    }

    Sentry.init({
        dsn: VITE_SENTRY_DSN,
        // Prevents a single user from spamming Sentry if they get stuck in an error loop
        sampleRate: 1.0,
        maxValueLength: 1000, // Caps the size of the error message

        // Set tracesSampleRate to 1.0 to capture 100% of transactions for performance monitoring.
        // Reduce in production to lower data volume (e.g., 0.1 for 10%)
        // Captures "transactions" (e.g., page loads, API calls, navigation events)
        // Which page is loading slowly....
        tracesSampleRate: 1,
        // Set profilesSampleRate to profile a subset of transactions
        // This requires the SDK to support profiling
        // Snapshot of exact code functions (which functions causes CPU to spike...)
        profilesSampleRate: 1,

        // Personaly Identifiable Information
        // Collects things like IP adresses, username (if exists in our system)...
        sendDefaultPii: true,

        // Environment name
        environment: import.meta.env.MODE,

        // Release version - use your app version from package.json
        release: `statotest-portal@${__APP_VERSION__}`,

        // Ignore common benign errors
        ignoreErrors: [
            // Browser extensions
            'top.GLOBALS',
            // Network errors that aren't actionable
            'Network Error',
            'NetworkError',
            // Random plugins/extensions
            // 'Can\'t find variable: ZiteReader',
            // 'jigsaw is not defined',
            // 'ComboSearch is not defined',
        ],

        // Filter out local development URLs
        denyUrls: [
            /extensions\//i,
            /^chrome:\/\//i,
            /^chrome-extension:\/\//i,
        ],

        // Relay sessions
        // "video-like" reconstruction of what the user did before the crash.
        replaysSessionSampleRate: 0.0, // do not record any samples of healthy sessions
        replaysOnErrorSampleRate: 1.0, // record every session with an error

        // Enable integrations for React
        integrations: [
            Sentry.browserTracingIntegration(),
            Sentry.replayIntegration({}),
        ],

        // Configure beforeSend to filter or modify events before sending
        beforeSend(event, hint) {
            // Filter out errors from browser extensions
            if (event.exception?.values) {
                const error = hint.originalException;
                if (error && typeof error === 'object' && 'message' in error) {
                    const message = String(error.message);
                    if (message.includes('extension') || message.includes('chrome-extension')) {
                        return null;
                    }
                }
            }
            return event;
        },
    });
};
